import type { ComponentPropsWithoutRef, Ref } from 'react';
import { cx } from '../cx';
import { Spinner } from '../Spinner/Spinner';
import { Drawing } from '../StatusSymbol/Drawing';
import type { DrawingName } from '../StatusSymbol/drawings';
import { LEGEND_FACTOR, StatusSymbol } from '../StatusSymbol/StatusSymbol';

/**
 * ONE ONBOARDING STEP CELL (redesign register D4, D5, D9, A9, A10, L2, L10, L11; brief
 * FE-FOUNDATION section 3; the drawings and states of `design-step-symbols.html` section 2).
 *
 * Seven states, every one readable at rest (L11): a grey ring that can be ticked; the ring with
 * "Due today" and no colour, because it is not late yet; the ring with the late symbol and
 * "N days late" in the late tone; the same with the waiting symbol in its own tone when the
 * client has to act (D4: a late step that waits on the client is named differently; register
 * A6-10 and decision page item 5.1, StatusSymbol role `waiting`, BR-SYMBOLS-4), whose word
 * "Waiting on client" the legend prints once (A6-6); the green tick; a grey ring with a dash and "Not needed" for a step that is no longer owed (D9:
 * skipped reads as skipped, not done); a dashed ring that cannot be ticked, with the reason.
 *
 * PRESENTATIONAL. The cell says what it is and calls `onActivate`; the write, its undo toast
 * and its refusal stay with the page, as `OnboardingChecklist.tsx` does today. The accessible
 * names are that file's too, so a screen reader hears the same sentence on every page.
 *
 * SIZE `f = 22/24` (README "Sizing", `--step-mark-size`), click area `--step-cell-hit`
 * (components page section 5, rule R42). A blocked cell is `aria-disabled`, not `disabled`:
 * a disabled button takes no focus, and the words that say why it cannot be ticked would be
 * unreachable by keyboard.
 *
 * THE ONE STEP TICK, AT THREE SIZES (BR-PARTS; DESIGN.md B1, B10, E4; register A5-2 "step
 * cells as drawn"). The checklist matrix, the client page's and the record page's step rows and
 * the legends drew three ticks of their own; they all draw this one now.
 *   - `cell` (default): the matrix cell above - the mark, its words under it, and the cell's box
 *     as the hit box (`--step-cell-hit`, grown to the touch floor by `.hit-expand`).
 *   - `row`: a step in a list row (a checklist under a client, the record page's checkpoints).
 *     The 22px mark alone, as a round button whose box IS the drawn mark and whose press grows
 *     to the touch floor without a painted pixel (`.hit-expand`, B19); the row prints the words
 *     in its own trailing slot. It is always a button, so a popover or a tooltip can wrap it
 *     (`ref` and the rest of a button's props pass through), and `busy` puts the spinner where
 *     the mark is while a write the caller has waited on is in flight.
 *   - `legend`: the symbol page's 16px size (`f = 2/3`, README "Sizing"), the drawing alone,
 *     decorative, standing on the word's x-height line (`vertical-align: middle`) in inline flow
 *     beside the word - never the cell's 36px box, which is what sat the legend crooked.
 *
 * THE DAY NUMBER BESIDE THE MARK (BR-ONBOARDING; register A6-6 and A7-R3, "lieber rechts
 * daneben statt drunter"). A `cell` given `days` prints a late step as the mark, then the late
 * symbol and the number alone in the late tone, on one line; the legend says once what the
 * number counts. A cell that stands at the start of its column (`align="start"`) draws its mark
 * at its leading edge and its words under it from there, so the marks of one column stay in one
 * line whatever stands beside or under them. Without either a cell is drawn as before: centred,
 * its `words` under the mark.
 */
export type StepMarkState =
  | 'open'
  | 'due-today'
  | 'late-us'
  | 'late-client'
  | 'done'
  | 'waived'
  | 'blocked';

export const STEP_MARK_STATES: readonly StepMarkState[] = [
  'open',
  'due-today',
  'late-us',
  'late-client',
  'done',
  'waived',
  'blocked',
];

/**
 * The words for a late step that waits on the client (register O4, resolved words), printed by
 * the Board, the Cards and the Dashboard; the step cell draws the waiting symbol instead, and the
 * legend names it once.
 */
export const WAITING_ON_CLIENT = 'Waiting on client';

const MARK: Readonly<Record<StepMarkState, DrawingName>> = {
  open: 'ring',
  'due-today': 'ring',
  'late-us': 'ring',
  'late-client': 'ring',
  done: 'done',
  waived: 'waived',
  blocked: 'blocked',
};

const MARK_INK: Readonly<Record<StepMarkState, string>> = {
  open: 'text-(--color-status-inactive)',
  'due-today': 'text-(--color-status-inactive)',
  'late-us': 'text-(--color-status-inactive)',
  'late-client': 'text-(--color-status-inactive)',
  done: 'text-(--color-status-done)',
  waived: 'text-(--color-status-inactive)',
  blocked: 'text-(--color-status-inactive)',
};

/** README "Sizing": the onboarding step cells take `f = 22/24` on the 24-unit circle family. */
export const STEP_MARK_FACTOR = 22 / 24;

export type StepMarkSize = 'cell' | 'row' | 'legend';

/** README "Sizing": a 16px legend mark on the 24-unit circle family. */
export const STEP_MARK_LEGEND_FACTOR = LEGEND_FACTOR;

/** What a `row` mark passes on to its button, so a popover or a tooltip can wrap it. */
type RowButtonProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  'children' | 'className' | 'type' | 'onClick'
> & {
  readonly onClick?: ComponentPropsWithoutRef<'button'>['onClick'] | undefined;
  readonly ref?: Ref<HTMLButtonElement> | undefined;
};

export interface StepMarkProps extends RowButtonProps {
  readonly state: StepMarkState;
  /** `cell` (default), `row` or `legend`; see the file header. */
  readonly size?: StepMarkSize | undefined;
  /**
   * A `row` mark that ticks and unticks says so with `aria-pressed` (the default). One whose
   * click on a done step opens something else (the record page's Change Date and Clear) is not
   * a toggle and passes `false`.
   */
  readonly toggle?: boolean | undefined;
  /**
   * The words under the mark, from `deadlineSentence` and the step actor map
   * (`app/fulfillment/shared/stepActors.ts`): "5 days late", "Due today", "Not needed",
   * "After the call". Null or absent prints nothing (an open step, a done step).
   */
  readonly words?: string | null | undefined;
  /**
   * A `cell`'s days late, printed beside the mark after the late symbol in place of `words`
   * (see the file header). Read for the two late states only; absent or null prints as before.
   */
  readonly days?: number | null | undefined;
  /** A `cell`'s alignment: `center` (default) or `start`, the mark at the leading edge. */
  readonly align?: 'center' | 'start' | undefined;
  /** The step's own label, "Introduction to community"; the accessible names are built from it. */
  readonly stepLabel: string;
  /** The day a done step was ticked, as the page prints it ("7 Sep"). */
  readonly doneOn?: string | null | undefined;
  /**
   * Called when the cell is ticked or unticked. Absent makes the cell read-only text (a person
   * who may not write, components page section 5 "Disabled"): no button, no hover.
   */
  readonly onActivate?: (() => void) | undefined;
  /**
   * A write in flight: the cell keeps its look and takes no second click. A `row` mark draws
   * the spinner in the mark's place instead (the record page's delayed spinner, which the caller
   * starts after its own 140 ms).
   */
  readonly busy?: boolean | undefined;
  readonly className?: string | undefined;
}

/** `OnboardingChecklist.tsx`'s three accessible names, so every page says the same sentence. */
export function stepMarkLabel(
  state: StepMarkState,
  stepLabel: string,
  doneOn?: string | null,
): string {
  if (state === 'done') return `${stepLabel}, done on ${doneOn ?? 'an unknown day'}`;
  if (state === 'blocked') return `${stepLabel} is not yet possible`;
  return `Mark ${stepLabel} done`;
}

/** Only a blocked step cannot be ticked; a waived one can, as on the checklist today. */
export function isStepTickable(state: StepMarkState): boolean {
  return state !== 'blocked';
}

/**
 * A late step's symbol and tone: the late clock for a step we owe, the waiting mark for one the
 * client owes (BR-SYMBOLS-4; the overview picture draws the cell's words in the waiting tone).
 */
function lateRole(state: StepMarkState): 'late' | 'waiting' {
  return state === 'late-client' ? 'waiting' : 'late';
}

const LATE_INK = {
  late: 'text-(--color-status-late)',
  waiting: 'text-(--color-status-waiting)',
} as const;

/** The late step's symbol and the number of days, in the step's late tone. */
function DaysLate({
  days,
  role,
  underline,
}: {
  readonly days: number;
  readonly role: 'late' | 'waiting';
  readonly underline: boolean;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-0.5 text-2xs leading-(--leading-footnote) font-semibold tabular-nums',
        LATE_INK[role],
        /* "A late cell under the pointer underlines its text" (components page section 5). */
        underline && 'group-hover:underline',
      )}
      data-step-days={String(days)}
    >
      <StatusSymbol status={role} />
      {days}
    </span>
  );
}

export function StepMark({
  state,
  size = 'cell',
  words,
  days,
  align = 'center',
  stepLabel,
  doneOn,
  onActivate,
  busy = false,
  toggle = true,
  className,
  ...button
}: StepMarkProps) {
  if (size === 'legend') {
    return (
      <Drawing
        name={MARK[state]}
        factor={STEP_MARK_LEGEND_FACTOR}
        unit="px"
        sizeName="legend"
        className={cx('align-middle', MARK_INK[state], className)}
      />
    );
  }
  if (size === 'row') {
    return (
      <RowMark
        state={state}
        stepLabel={stepLabel}
        doneOn={doneOn}
        onActivate={onActivate}
        busy={busy}
        toggle={toggle}
        className={className}
        button={button}
      />
    );
  }
  const late = state === 'late-us' || state === 'late-client';
  const tickable = isStepTickable(state);
  const interactive = onActivate !== undefined && tickable && !busy;
  const lateDays = late && days !== undefined && days !== null ? days : null;
  const mark = (
    <Drawing
      name={MARK[state]}
      factor={STEP_MARK_FACTOR}
      unit="px"
      className={cx(
        MARK_INK[state],
        /* Components page section 5: an open cell under the pointer draws its ring in the
           accent, the one interactive colour (L6). */
        interactive && (state === 'open' || state === 'due-today') && 'group-hover:text-accent',
      )}
    />
  );
  const content = (
    <>
      {lateDays === null ? (
        mark
      ) : (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          {mark}
          <DaysLate days={lateDays} role={lateRole(state)} underline={interactive} />
        </span>
      )}
      {lateDays === null && words !== undefined && words !== null && words !== '' && (
        <span
          className={cx(
            'text-2xs leading-(--leading-footnote)',
            align === 'start' ? 'text-left' : 'text-center',
            /* "A late cell under the pointer underlines its text" (components page section 5). */
            interactive && late && 'group-hover:underline',
            late
              ? LATE_INK[lateRole(state)]
              : state === 'due-today'
                ? 'text-ink'
                : 'text-ink-muted',
          )}
          data-step-words=""
        >
          {late && <StatusSymbol status={lateRole(state)} className="mr-0.5" />}
          {words}
        </span>
      )}
    </>
  );
  const frame = cx(
    'inline-flex min-h-(--step-cell-hit) min-w-(--step-cell-hit) flex-col',
    align === 'start' ? 'items-start' : 'items-center',
    'justify-start gap-0.5 rounded-control px-1 py-1',
    className,
  );

  if (onActivate === undefined) {
    return (
      <span className={frame} data-step-mark={state}>
        {content}
      </span>
    );
  }
  return (
    <button
      type="button"
      data-step-mark={state}
      aria-label={stepMarkLabel(state, stepLabel, doneOn)}
      aria-pressed={state === 'done'}
      aria-disabled={tickable ? undefined : true}
      aria-busy={busy || undefined}
      {...(interactive ? { onClick: onActivate } : {})}
      className={cx(
        frame,
        /* The cell is drawn at the components page's 36 (R42); `.hit-expand` grows what the
           pointer and the finger can press to the product's touch floor (DESIGN.md criterion
           1, 40 on the desktop and 44 on a phone) without a painted pixel. */
        'hit-expand group cursor-default transition-colors duration-(--duration-fast)',
        interactive &&
          (state === 'open' || state === 'due-today'
            ? 'hover:bg-accent-subtle'
            : 'hover:bg-surface-hover'),
      )}
    >
      {content}
    </button>
  );
}

/**
 * THE ROW SIZE: the mark is the button. Interactive cues are the cell's (an open ring under the
 * pointer draws in the accent on the accent's subtle fill, components page section 5), the focus
 * ring follows the drawn circle, and a blocked mark is `aria-disabled` for the cell's reason.
 */
function RowMark({
  state,
  stepLabel,
  doneOn,
  onActivate,
  busy,
  toggle,
  className,
  button,
}: {
  readonly state: StepMarkState;
  readonly stepLabel: string;
  readonly doneOn: string | null | undefined;
  readonly onActivate: (() => void) | undefined;
  readonly busy: boolean;
  readonly toggle: boolean;
  readonly className: string | undefined;
  readonly button: RowButtonProps;
}) {
  const tickable = isStepTickable(state);
  const { onClick, ...rest } = button;
  const handler = tickable && !busy ? (onActivate ?? onClick) : undefined;
  const ringed = state === 'open' || state === 'due-today';
  return (
    <button
      type="button"
      data-step-mark={state}
      data-size="row"
      aria-label={stepMarkLabel(state, stepLabel, doneOn)}
      {...(toggle ? { 'aria-pressed': state === 'done' } : {})}
      aria-disabled={tickable ? undefined : true}
      aria-busy={busy || undefined}
      {...rest}
      {...(handler === undefined ? {} : { onClick: handler })}
      className={cx(
        'hit-expand group relative inline-flex size-(--step-mark-size) shrink-0 cursor-default',
        'items-center justify-center rounded-full transition-colors duration-(--duration-fast)',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        handler !== undefined && !rest.disabled && ringed && 'hover:bg-accent-subtle',
        className,
      )}
    >
      {busy ? (
        <Spinner size="0.85rem" />
      ) : (
        <Drawing
          name={MARK[state]}
          factor={STEP_MARK_FACTOR}
          unit="px"
          sizeName="row"
          className={cx(
            MARK_INK[state],
            handler !== undefined && !rest.disabled && ringed && 'group-hover:text-accent',
          )}
        />
      )}
    </button>
  );
}
